import React, {FC, useEffect, useState} from "react";
import {Alert, Button, Space, Typography, App} from "antd";
import {useTranslation} from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {AdminPage, PageHeader} from "../../Components/ui";
import {
    useCurrentUserProfileQuery,
    useUpdateCurrentUserProfileMutation
} from "../../GraphQL/generated/graphql";

const richTextModules = {
    toolbar: [
        ["bold", "italic", "underline"],
        [{list: "ordered"}, {list: "bullet"}],
        ["link"],
        ["clean"]
    ]
};

const richTextFormats = [
    "bold",
    "italic",
    "underline",
    "list",
    "bullet",
    "link"
];

export const UserProfilePage: FC = () => {
    const {message} = App.useApp();
    const {t} = useTranslation();
    const errorLabels = buildUserFacingErrorLabels(t, t("profile-error", {
        defaultValue: "Profile is currently unavailable."
    }));
    const [emailSignature, setEmailSignature] = useState("");
    const profileQuery = useCurrentUserProfileQuery({
        fetchPolicy: "cache-and-network"
    });
    const [updateCurrentUserProfile, updateState] = useUpdateCurrentUserProfileMutation();
    const profile = profileQuery.data?.currentUserProfile;

    useEffect(() => {
        setEmailSignature(profile?.emailSignature ?? "");
    }, [profile?.emailSignature]);

    const handleSave = async () => {
        try {
            const result = await updateCurrentUserProfile({
                variables: {
                    input: {
                        emailSignature
                    }
                }
            });
            const updatedProfile = result.data?.updateCurrentUserProfile;
            if (updatedProfile) {
                setEmailSignature(updatedProfile.emailSignature ?? "");
            }

            message.success(t("profile-save-success", {defaultValue: "Profile saved."}));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    };

    return (
        <AdminPage>
            <PageHeader title={t("profile", {defaultValue: "Profile"})}/>
            <div className="ati-stack ati-stack--narrow">
                {profileQuery.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("profile-load-error-title", {defaultValue: "Profile unavailable"})}
                        description={getUserFacingErrorMessage(profileQuery.error, t, errorLabels)}
                    />
                )}

                <Space direction="vertical" size={4}>
                    <Typography.Text strong>{t("email", {defaultValue: "Email"})}</Typography.Text>
                    <Typography.Text>{profile?.email || profile?.userName || "-"}</Typography.Text>
                </Space>

                <div className="ati-stack ati-stack--tight">
                    <Typography.Text strong>{t("email-signature", {defaultValue: "Email signature"})}</Typography.Text>
                    <ReactQuill
                        {...({"aria-label": t("email-signature", {defaultValue: "Email signature"})} as Record<string, string>)}
                        theme="snow"
                        value={emailSignature}
                        modules={richTextModules}
                        formats={richTextFormats}
                        onChange={setEmailSignature}
                    />
                </div>

                <Space>
                    <Button
                        type="primary"
                        loading={updateState.loading}
                        disabled={profileQuery.loading && !profile}
                        onClick={() => void handleSave()}
                    >
                        {t("save-profile", {defaultValue: "Save profile"})}
                    </Button>
                </Space>
            </div>
        </AdminPage>
    );
};

export default UserProfilePage;
